import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { add } from "date-fns";

import {
  FormLabel,
  FormSelect,
  FormRadio,
  FormIconChooser,
  BaseForm,
  FormInputHolder
} from '../form';
import { checkTime, required } from '../../helpers/formValidators';

import Duration, { addDateAndTimeFields, getDurationStrict } from './Duration';
import babyIcons from '../icons/babyIcons';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import { Error } from '../form/BaseForm';
import { FormSpy } from 'react-final-form';
import { PrimaryButton, SecondaryButton } from '../buttons';
import { FormContext } from '../../context/form-context';
import { OnChange } from 'react-final-form-listeners';

const SleepForm = (props) => {
  const {
    selectedProfile,
    babies,
    profileOptions,
    sleepRecordsPost,
    sleepRecordsPatch,
    accountId,
    hideTrackerModals,
    handleRecords,
    showUnSavedModal,
    showSaveEntryModal,
    showFirstTimeSavingModal
  } = props;

  const defaultValues = {
    babyId: null,
    providerId: selectedProfile.id,
    date: new Date(),
    startTime: new Date(),
    endTime: add(new Date(), { minutes: 10 })
  }

  const [initialValues, setInitialValues] = useState(undefined);

  const formContext = useContext(FormContext);

  const toggleWarningModal = () => {
    return formContext.isDirty ? showUnSavedModal() : hideTrackerModals();
  };

  useEffect(() => {
    // if there's form data in the context, use that instead of using default values
    if (formContext && Object.keys(formContext.formValues).length > 0) {
      setInitialValues(formContext.formValues);
      return
    }

    const latestRecord = handleRecords('sleep', null) || {};

    setInitialValues({
      ...defaultValues,
      ...latestRecord
    });
  }, []);

  const onSubmit = values => {
    const recordBody = {
      ...values,
      providerId: values.providerId === "other" ? null : values.providerId,
      otherProvider: values.providerId !== "other" ? null : values.otherProvider,
      date: addDateAndTimeFields(values.date, values.startTime),
      duration: getDurationStrict(values.startTime, values.endTime),
      endTime: addDateAndTimeFields(values.date, values.endTime)
    };

    if (values.id) {
      // Update existing record
      return sleepRecordsPatch(
        { accountId: accountId, babyId: values.babyId, id: values.id },
        { body: JSON.stringify(recordBody) }
      ).then(data => {
        if (data) {
          hideTrackerModals();
        }
      }).catch(error => <ErrorMessage />);
    } else {
      // Create new record
      return sleepRecordsPost(
        { accountId: accountId, babyId: babies.length === 1 ? babies[0].id : values.babyId },
        { body: JSON.stringify(recordBody) }
      ).then((data) => {
        if (data) {
          hideTrackerModals();
        }
      }).catch(error => <ErrorMessage />);
    }
  };

  const onSave = (values) => {
    if (!showFirstTimeSavingModal) {
      onSubmit(values);
    } else {
      formContext.setValues(values);
      formContext.setFunctions({
        onSubmit: onSubmit,
      });

      return showSaveEntryModal();
    }
  }

  if (!initialValues) {
    return <div></div>
  }

  return (
    <BaseForm
      className="sleepFormModal"
      onSubmit={onSave}
      validate={checkTime}
      initialValues={initialValues}
    >
      <h2 className="title">
        Track Sleep
      </h2>

      {babies && babies.length > 1 && (
        <React.Fragment>
          <FormIconChooser>
            {babies.map(baby => (
              <FormRadio
                key={baby.id}
                value={baby.id}
                name="babyId"
                imageSrc={babyIcons[baby.icon]}
                validate={required}
              >
                {baby.name}
              </FormRadio>
            ))}
          </FormIconChooser>

          <OnChange name="babyId">
            {(value, previous) => {
              if (value === previous) {
                return;
              }

              const latestRecord = handleRecords('sleep', value) || {};

              setInitialValues({
                ...defaultValues,
                ...latestRecord,
                babyId: value
              });
            }}
          </OnChange>

          <Error name="babyId" />
        </React.Fragment>
      )}

      <FormLabel>Provider</FormLabel>
      <FormSelect
        placeholder={'Choose Provider...'}
        name="providerId"
        options={profileOptions}
        includeOther
        otherName="otherProvider"
      />

      <Duration />

      <FormSpy subscription={{ submitting: true }}>
        {({ submitting }) => {
          return (
            <FormInputHolder>
              <SecondaryButton
                type="button"
                label="Cancel"
                onClick={toggleWarningModal}
              />

              <PrimaryButton type="submit" label="Save" disabled={submitting} />
            </FormInputHolder>
          );
        }}
      </FormSpy>

      <FormSpy
        subscription={{ dirty: true, dirtyFields: true, visited: true, values: true }}
        onChange={({ dirty, visited, values }) => {
          if (dirty) {
            let actuallyDirty = false;

            formContext.setValues(values);

            Object.keys(visited).forEach((key) => {
              if (visited[key]) {
                actuallyDirty = true;
              }
            });

            actuallyDirty ? formContext.setDirty() : formContext.notDirty();
          }
        }}
      />
    </BaseForm>
  );
}

SleepForm.defaultProps = {
  error: null,
  profiles: [],
  selectedProviderId: null,
  handleSubmit: () => {},
  t: () => {}
};

SleepForm.propTypes = {
  error: PropTypes.string,
  submitting: PropTypes.bool,
  babies: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string
    })
  ).isRequired,
  profiles: PropTypes.arrayOf(PropTypes.object),
  selectedProviderId: PropTypes.string,
  handleSubmit: PropTypes.func,
  showSaveEntryModal: PropTypes.func,
  showFirstTimeSavingModal: PropTypes.bool,
  t: PropTypes.func
};

export default SleepForm;



// WEBPACK FOOTER //
// ./src/components/ActivityTracker/SleepForm.js